import Highcharts from 'highcharts';
require('highcharts/modules/exporting')(Highcharts);
import canvg from 'canvg';
import { updateCharts } from '../reducers/mapview/actions';
import {
  chartColorScheme,
  innerDonutSeriesColors,
  legendIndexForDonutChart,
} from '../config/staticText.config';
import store from '../store/store';

const generateColor = (lookupValue) => chartColorScheme[lookupValue];

const calculateLegendIndex = (key) => {
  if (
    key === 'Federal Land' ||
    key === 'Tribal' ||
    key === 'Private/Other' ||
    key === 'State Land'
  ) {
    return 1;
  } else {
    return 0;
  }
};

const convertChartToURI = (chart, id) => {
  // React PDF requires a data uri to be passed into the <Image src='DATAURI' /> tags to render the image.
  // Highcharts does not allow us to export the charts to a PNG or JPG URI, but allows us to convert to SVG
  // We convert each chart to an SVG, then convert the SVGs to a canvas, and then convert the canvas into a data uri.
  const existingChartURIs = store.getState().mapview.charts;
  const chartCanvas = document.createElement('CANVAS');
  document.body.appendChild(chartCanvas);
  canvg(chartCanvas, chart.getSVG());
  const img = chartCanvas.toDataURL('image/png');

  const chartURI = {
    id,
    uri: img,
  };
  existingChartURIs[id] = chartURI;
  store.dispatch(updateCharts(existingChartURIs));

  // Remove the DOM elements we created to create the charts but don't actually need in the application.
  if (document.getElementById('highcharts-div')) {
    const temporaryHighchartsDOM = document.getElementById('highcharts-div');
    temporaryHighchartsDOM.parentNode.removeChild(temporaryHighchartsDOM);
  }
  if (chartCanvas) {
    chartCanvas.parentNode.removeChild(chartCanvas);
  }
};

const renderDonutChart = (chart, seriesData) => {
  const total = seriesData[seriesData.length - 1].total;
  const intactSubtotal = seriesData[seriesData.length - 1].intact;
  const plowprintSubtotal = seriesData[seriesData.length - 1].plowprint;
  const waterSubtotal = seriesData[seriesData.length - 1].water;
  const developedSubtotal = seriesData[seriesData.length - 1].developed;

  const jointSeriesData = [];

  const percentIntact = Number(((intactSubtotal / total) * 100).toFixed(1));
  const percentPlowprint = Number(
    ((plowprintSubtotal / total) * 100).toFixed(1)
  );
  const percentWater = Number(((waterSubtotal / total) * 100).toFixed(1));
  const percentDeveloped = Number(
    ((developedSubtotal / total) * 100).toFixed(1)
  );

  const outterSeriesData = [
    {
      name: 'Intact Extent',
      color: '#4ca64c',
      y: percentIntact,
    },
    {
      name: 'Plowprint Extent',
      color: '#cf5c36',
      y: percentPlowprint,
    },
    {
      name: 'Open Water',
      color: '#015BE7',
      y: percentWater,
    },
    {
      name: 'Developed',
      color: '#FEFC03',
      y: percentDeveloped,
    },
  ];

  seriesData.pop(); // Remove the last item which was the subtotals
  const innerSeriesData = seriesData.map((dataObject, i) => {
    return {
      name: dataObject.name,
      color: innerDonutSeriesColors[i],
      y: Number(((dataObject.y / total) * 100).toFixed(1)),
      legendIndex: legendIndexForDonutChart[dataObject.name],
    };
  });

  // Client asked us to have 'developed land' appear on the right column of the chart legend.
  // We add a blank placeholder in the previous index to ensure the developed land item is placed properly
  // We add an edge case to the labelFormatted below to return nothing in the legend.
  const indexOfDevelopedLand = innerSeriesData.indexOf(
    innerSeriesData.find(({ name }) => name === 'Developed Land')
  );

  const dummyData = {
    name: '',
    color: 'white',
    y: 0,
    legendIndex: 12,
  };
  innerSeriesData.splice(indexOfDevelopedLand, 0, dummyData);

  // In order to to prevent duplicated values in the legend, we need to push dummy data into the innerSeries array, and override the legend label in the chart options,
  innerSeriesData.push({
    name: 'Intact Extent',
    color: '#4ca64c',
    y: 0,
    legendIndex: 0,
  });

  innerSeriesData.push({
    name: 'Plowprint Extent',
    color: '#cf5c36',
    y: 0,
    legendIndex: 1,
  });

  const outterSeriesDataObject = {
    name: 'outterData',
    data: outterSeriesData,
    innerSize: '70%',
    showInLegend: false, // to prevent duplicated items in legend
  };

  const innerSeriesDataObject = {
    name: 'innerData',
    data: innerSeriesData.filter((data) => data !== undefined),
  };

  jointSeriesData.push(innerSeriesDataObject); // Inner data should be the first index in series
  jointSeriesData.push(outterSeriesDataObject);

  const chartDiv = document.createElement('DIV');
  chartDiv.setAttribute('id', 'highcharts-div');
  document.body.appendChild(chartDiv);
  const newChart = Highcharts.chart({
    chart: {
      renderTo: chartDiv,
      plotBackgroundColor: null,
      plotBorderWidth: 0,
      plotShadow: false,
      type: 'pie',
    },
    credits: {
      enabled: false,
    },
    legend: {
      margin: 2,
      layout: 'horizontal',
      itemWidth: 225,
      labelFormatter: function () {
        let label;
        if (this.name.includes('/')) {
          const suffix = this.name.slice(this.name.indexOf('('));
          label = this.name.slice(0, this.name.indexOf('/')) + ' ' + suffix;
        } else {
          label = this.name;
        }

        // We are overriding the y values of the dummy data to prevent duplicated values from showing up in the legend.
        if (this.name === '') {
          // Used to ensure "developed land" appears on the right side column
          return null;
        } else if (this.name === 'Plowprint Extent') {
          return `${label}: ${percentPlowprint}%`;
        } else if (this.name === 'Intact Extent') {
          return `${label}: ${percentIntact}%`;
        } else {
          return `${label}: ${this.y}%`;
        }
      },
      style: {
        fontFamily: 'Roboto, sans-serif',
      },
    },
    title: {
      text: chart.leftYAxisLegend,
      align: 'center',
      style: {
        color: 'black',
        fontSize: '24px',
        fontFamily: 'Roboto, sans-serif',
      },
    },
    plotOptions: {
      pie: {
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
        startAngle: -90,
        endAngle: 90,
        center: ['50%', '105%'],
        size: '170%',
      },
    },
    series: jointSeriesData,
  });

  convertChartToURI(newChart, chart.id);
};

const renderSplineOrColumnChart = (chart, data, start, end) => {
  let yAxisTitleRight = 'Total Acerage (acres)';
  if (chart.title === 'Plowprint Expansion') {
    yAxisTitleRight = ''; // This chart does not receive a yAxisTitleRight
    if (start < 2010) {
      start += 1; // This chart goes from 2010 onwards, not 2009
    }
  } else {
    const splineDataObject = data.find(({ name }) => name === 'Total Acreage');
    if (splineDataObject) {
      const maxAcreage = Math.max.apply(null, splineDataObject.data);
      if (maxAcreage >= 1000000) {
        yAxisTitleRight = 'Total Acreage (millions of acres)';
      } else if (maxAcreage >= 10000) {
        yAxisTitleRight = 'Total Acreage (thousands of acres)';
      }
    }
  }

  const chartDiv = document.createElement('DIV');
  chartDiv.setAttribute('id', 'highcharts-div');
  document.body.appendChild(chartDiv);
  const newChart = Highcharts.chart({
    chart: {
      renderTo: chartDiv,
    },
    title: {
      text: chart.title,
      style: {
        color: 'black',
        fontSize: '24px',
        fontFamily: 'Roboto, sans-serif',
      },
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      labels: {
        style: {
          color: 'black',
          fontSize: '11px',
          fontFamily: 'Roboto, sans-serif',
        },
      },
      categories: chart.years
        .filter((year) => year >= start && year <= end)
        .sort(),
    },
    yAxis: [
      {
        min: 0,
        max: 100,
        labels: {
          format: '{value}%',
          style: {
            color: 'black',
            fontSize: '11px',
            fontFamily: 'Roboto, sans-serif',
          },
        },
        title: {
          text:
            chart.title === 'Plowprint Expansion'
              ? 'Expansion by Crop Type'
              : 'Percent by Land Type',
          style: {
            color: 'black',
            fontSize: '14px',
            fontFamily: 'Roboto, sans-serif',
          },
        },
      },
      {
        title: {
          text: chart.title === 'Plowprint Expansion' ? '' : yAxisTitleRight,
          style: {
            color: 'black',
            fontSize: '14px',
            fontFamily: 'Roboto, sans-serif',
          },
        },
        labels: {
          formatter: function () {
            let value;
            switch (yAxisTitleRight) {
              case 'Total Acreage (millions of acres)':
                value = this.value / 1000000;
                break;
              case 'Total Acreage (thousands of acres)':
                value = this.value / 1000;
                break;
              default:
                value = this.value;
                break;
            }
            return value.toFixed(1).toLocaleString();
          },
          style: {
            color: 'black',
          },
        },
        opposite: true,
      },
    ],
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
        stacking: 'normal',
      },
    },
    series: data,
  });

  convertChartToURI(newChart, chart.id);
};

const formatDataByCategory = (chart, histogramData) => {
  const seriesData = {
    years: [],
  };

  histogramData
    .filter((row) => chart.histogramReferences.includes(row.dataCategory))
    .sort((a, b) => {
      if (a.dataYear > b.dataYear) {
        return 1;
      } else if (a.dataYear < b.dataYear) {
        return -1;
      } else {
        return 0;
      }
    })
    .forEach((row) => {
      let keyName;
      if (row.dataSubcategory === 'NA' || row.dataSubcategory === 'N/A') {
        keyName = row.dataCategory;
      } else {
        keyName = row.dataSubcategory;
      }

      if (!seriesData[keyName]) {
        seriesData[keyName] = [Math.round(row.acreCount)];
      } else {
        seriesData[keyName].push(Math.round(row.acreCount));
      }

      if (!seriesData.years.includes(row.dataYear)) {
        seriesData.years.push(row.dataYear);
      }
    });

  return seriesData;
};

const calculateCategoryTotalsByYear = (categoryData) => {
  const annualTotals = [];
  categoryData.years.forEach((year, yearIndex) => {
    const total = Object.keys(categoryData)
      .filter((category) => category !== 'years')
      .reduce(
        (accumulator, currentValue) =>
          accumulator + categoryData[currentValue][yearIndex],
        0
      );
    annualTotals.push(total);
  });

  return annualTotals;
};

export const createColumnCharts = (chart, seriesData, startYear, endYear) => {
  const categoryData = formatDataByCategory(chart, seriesData);
  const yearData = {};
  const annualTotals = [];
  categoryData.years.forEach((year, yearIndex) => {
    let total = 0;
    Object.keys(categoryData)
      .filter((key) => key !== 'years')
      .forEach((key) => {
        total = total + categoryData[key][yearIndex];
      });
    annualTotals.push(total);
    Object.keys(categoryData)
      .filter((key) => key !== 'years')
      .forEach((key) => {
        const percentageValue = (categoryData[key][yearIndex] / total) * 100;
        if (!yearData[key]) {
          yearData[key] = [];
        }
        yearData[key].push(percentageValue);
      });
  });

  const formattedSeriesData = [];
  Object.keys(yearData).forEach((key) => {
    const seriesObject = {
      name: key,
      type: 'column',
      color: generateColor(key),
      data: yearData[key],
    };
    formattedSeriesData.push(seriesObject);
  });

  const filteredData = formattedSeriesData.filter(
    (item) => item.name !== 'key'
  );
  renderSplineOrColumnChart(chart, filteredData, startYear, endYear);
};

export const createColumnAndSplineChart = (
  chart,
  histogramData,
  startYear,
  endYear
) => {
  const combinedSeriesData = [];
  chart.histogramReferences.forEach((reference) => {
    const copyOfChart = JSON.parse(JSON.stringify(chart));
    copyOfChart.histogramReferences = reference;

    const formattedDataByCategory = formatDataByCategory(
      copyOfChart,
      histogramData
    );

    const categoryTotalsByYear = calculateCategoryTotalsByYear(
      formattedDataByCategory
    );

    const yearData = {};
    formattedDataByCategory.years.forEach((year, yearIndex) => {
      Object.keys(formattedDataByCategory)
        .filter((key) => key !== 'years' && key !== 'Cumulative Plowprint')
        .forEach((key) => {
          const percentageValue = Math.round(
            (formattedDataByCategory[key][yearIndex] /
              categoryTotalsByYear[yearIndex]) *
              100
          );
          if (!yearData[key]) {
            yearData[key] = [];
          }

          yearData[key].push(isNaN(percentageValue) ? 0 : percentageValue);
        });
    });

    yearData.annualTotals = categoryTotalsByYear;
    combinedSeriesData.push(yearData);
  });

  const cleanData = [];
  combinedSeriesData.forEach((objectOfArrays, objectIndex) => {
    Object.keys(objectOfArrays)
      .filter((key) => key !== 'Intact Extent')
      .map((key) => {
        const seriesObject = {};
        seriesObject.color = generateColor();
        seriesObject.data = objectOfArrays[key];
        if (key === 'annualTotals') {
          seriesObject.name = 'Total Acreage';
          seriesObject.type = 'spline';
          seriesObject.legendIndex = 0;
          seriesObject.yAxis = 1;
          seriesObject.color = chartColorScheme.annualTotals;
          cleanData.push(seriesObject);
        } else {
          seriesObject.name = key;
          seriesObject.yAxis = 0;
          seriesObject.legendIndex = calculateLegendIndex(key);
          seriesObject.stack = objectIndex;
          seriesObject.type = 'column';
          seriesObject.color = chartColorScheme[key];
          cleanData.push(seriesObject);
        }
      });
  });

  const col1 = cleanData.filter((item) => item.name !== 'Total Acreage');

  let splineData;
  if (chart.histogramReferences.includes('Intact Extent')) {
    splineData = histogramData
      .filter(
        (item) =>
          item.dataCategory === 'Intact Extent' &&
          item.dataYear >= startYear &&
          item.dataYear <= endYear
      )
      .map((item) => item.acreCount);
  } else {
    // It's cumulative plowprint
    splineData = histogramData
      .filter(
        (item) =>
          item.dataCategory === 'Cumulative Plowprint' &&
          item.dataYear >= startYear &&
          item.dataYear <= endYear
      )
      .map((item) => item.acreCount);
  }
  const splineObject = {
    data: splineData,
    legendIndex: 0,
    name: 'Total Acreage',
    yAxis: 1,
  };

  col1.push(splineObject);
  const highchartSeriesData = col1.filter((item) => item.name !== 'N/A');

  renderSplineOrColumnChart(chart, highchartSeriesData, startYear, endYear);
};

export const createDonutChart = (chart, seriesData, endYear) => {
  // Isolate each category's data
  const intactData = seriesData.filter(
    (data) =>
      data.dataCategory === 'Intact Habitat' && data.dataYear === endYear
  );

  const plowprintData = seriesData.filter(
    (data) =>
      data.dataCategory === 'Plowprint Composition' && data.dataYear === endYear
  );

  const waterData = seriesData.find(
    (data) => data.dataCategory === 'Open Water'
  );

  const developedData = seriesData.find(
    (data) => data.dataCategory === 'Developed'
  );

  let intactSubtotal = 0;
  let plowprintSubtotal = 0;

  intactData.forEach((data) => {
    intactSubtotal += Math.round(data.acreCount);
  });

  plowprintData.forEach((data) => {
    plowprintSubtotal += Math.round(data.acreCount);
  });

  const formattedDataForSeries = [];
  intactData.forEach((category) => {
    const seriesObject = {
      name: category.dataSubcategory + ' (Intact)',
      color: chartColorScheme[category.dataSubcategory],
      y: category.acreCount,
    };
    formattedDataForSeries.push(seriesObject);
  });

  plowprintData.forEach((category) => {
    const seriesObject = {
      name: category.dataSubcategory + ' (Plowprint)',
      color: chartColorScheme[category.dataSubcategory],
      y: category.acreCount,
    };
    formattedDataForSeries.push(seriesObject);
  });

  const waterSeriesObject = {
    name: 'Open Water',
    color: chartColorScheme.water,
    y: waterData.acreCount,
  };

  const developedSeriesObject = {
    name: 'Developed Land',
    color: chartColorScheme.developed,
    y: developedData.acreCount,
  };

  const subTotals = {
    intact: intactSubtotal,
    plowprint: plowprintSubtotal,
    water: waterData.acreCount,
    developed: developedData.acreCount,
    total: Math.round(
      intactSubtotal +
        plowprintSubtotal +
        waterData.acreCount +
        developedData.acreCount
    ),
  };

  formattedDataForSeries.push(waterSeriesObject);
  formattedDataForSeries.push(developedSeriesObject);
  formattedDataForSeries.push(subTotals);

  renderDonutChart(chart, formattedDataForSeries);
};

const createHorizontalChart = (chart, seriesData) => {
  const chartDiv = document.createElement('DIV');
  chartDiv.setAttribute('id', 'highcharts-div');
  document.body.appendChild(chartDiv);
  const newChart = Highcharts.chart({
    chart: {
      renderTo: chartDiv,
      type: 'bar',
      height: '200',
      width: '530',
      color: 'black',
    },
    credits: {
      enabled: false,
    },
    title: {
      text: '',
      x: 25,
      style: {
        color: 'black',
        fontSize: '14px',
        fontFamily: 'Roboto, sans-serif',
      },
    },
    plotOptions: {
      series: {
        stacking: 'normal',
      },
    },
    yAxis: {
      min: 0,
      max: 100,
      endOnTick: true,
      labels: {
        enabled: false,
        format: '{value}%',
        style: {
          color: 'black',
          fontSize: '10px',
          fontFamily: 'Roboto, sans-serif',
        },
      },
      title: '',
    },
    xAxis: {
      enabled: false,
      categories: ['', ''],
      labels: {
        style: {
          color: 'black',
          fontSize: '10px',
          fontFamily: 'Roboto, sans-serif',
        },
      },
    },
    legend: {
      enabled: false,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      floating: false,
      x: 50,
      borderWidth: 1,
      backgroundColor:
        Highcharts.defaultOptions.legend.backgroundColor || '#FFFFFF',
      color: 'black',
      itemStyle: {
        fontSize: '10px',
        fontFamily: 'Roboto, sans-serif',
        lineHeight: '12px',
        fontWeight: 400,
      },
    },
    series: seriesData,
  });
  convertChartToURI(newChart, chart.id);
};

export const createHorizontalBarChart = (chart, histogramData, customShape) => {
  const seriesData = formatDataByCategory(chart, histogramData);

  let total = 0;
  Object.keys(seriesData).forEach((key) => {
    if (!isNaN(seriesData[key][0])) {
      total = total + seriesData[key][0];
    }
  });

  let total2 = 0;
  Object.keys(seriesData).forEach((key) => {
    if (!isNaN(seriesData[key][1])) {
      total2 = total2 + seriesData[key][1];
    }
  });

  const filteredSeriesData = Object.keys(seriesData)
    .filter((seriesObject) => seriesObject !== 'years')
    .map((key, i) => {
      let arrayOfValues;
      if (customShape) {
        const value1 = isNaN((seriesData[key][1] / total2) * 100)
          ? 0
          : (seriesData[key][1] / total2) * 100;
        const value2 = isNaN((seriesData[key][0] / total) * 100)
          ? 0
          : (seriesData[key][0] / total) * 100;
        arrayOfValues = [value1, value2];
      } else {
        const value1 = isNaN((seriesData[key][0] / total) * 100)
          ? 0
          : (seriesData[key][0] / total) * 100;
        const value2 = isNaN((seriesData[key][1] / total2) * 100)
          ? 0
          : (seriesData[key][1] / total2) * 100;
        arrayOfValues = [value1, value2];
      }

      return {
        name: key,
        category: 0,
        data: arrayOfValues,
        legendIndex: i,
        color: chartColorScheme[key],
      };
    })
    .reverse();

  createHorizontalChart(chart, filteredSeriesData);
};

export const calculatePlowprintArea = async (data, endYear) => {
  const area = data.find(
    (row) =>
      row.dataCategory === 'Cumulative Plowprint' && row.dataYear === endYear
  ).acreCount;
  const existingChartURIs = store.getState().mapview.charts;
  existingChartURIs.totalPlowprintArea = Math.round(area);
  store.dispatch(updateCharts(existingChartURIs));
};

export const calculateIntactArea = async (data, endYear) => {
  const area = data.find(
    (row) => row.dataCategory === 'Intact Extent' && row.dataYear === endYear
  ).acreCount;
  const existingChartURIs = store.getState().mapview.charts;
  existingChartURIs.totalIntactArea = Math.round(area);
  store.dispatch(updateCharts(existingChartURIs));
};

export const calculatePlowprintExpansion = async (data, startYear, endYear) => {
  const endingArea = data.find(
    (row) =>
      row.dataCategory === 'Cumulative Plowprint' && row.dataYear === endYear
  ).acreCount;

  const beginningArea = data.find(
    (row) =>
      row.dataCategory === 'Cumulative Plowprint' && row.dataYear === startYear
  ).acreCount;
  const existingChartURIs = store.getState().mapview.charts;
  existingChartURIs.totalExpansionArea = Math.round(endingArea - beginningArea);
  store.dispatch(updateCharts(existingChartURIs));
};
